import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Snackbar,
  Alert,
  Paper,
  MenuItem,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Divider
} from '@mui/material';
import { sendGeneralNotification, getAllQuizzes } from '../services/api';

interface Quiz {
  _id: string;
  title: string;
  username?: string;
  organizer?: { firstName: string; lastName: string; username: string };
  category?: string;
  pricing?: { type: string; amount: number };
  dates?: { startDate: string; endDate: string };
  status?: string;
}

const QuizNotificationPage: React.FC = () => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [image, setImage] = useState('');
  const [quizId, setQuizId] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [fetchingQuizzes, setFetchingQuizzes] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    organizer: '',
    pricing: '',
    status: '',
    dateRange: ''
  });

  const fetchQuizzes = async (filterParams?: any) => {
    setFetchingQuizzes(true);
    try {
      const res: any = await getAllQuizzes(filterParams || filters);
      console.log('Quizzes API response:', res);
      if (res.success && Array.isArray(res.data)) {
        setQuizzes(res.data);
        console.log('Set quizzes:', res.data);
      } else {
        console.log('Failed to get quizzes:', res);
        setError('Failed to fetch quizzes');
      }
    } catch (err: any) {
      console.error('Error fetching quizzes:', err);
      setError('Error fetching quizzes: ' + err.message);
    } finally {
      setFetchingQuizzes(false);
    }
  };

  useEffect(() => {
    fetchQuizzes();
  }, []);

  const handleFilterChange = (field: string, value: string) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    fetchQuizzes(newFilters);
  };

  const clearFilters = () => {
    const clearedFilters = {
      search: '',
      category: '',
      organizer: '',
      pricing: '',
      status: '',
      dateRange: ''
    };
    setFilters(clearedFilters);
    fetchQuizzes(clearedFilters);
  };

  const handleQuizSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedId = e.target.value;
    setQuizId(selectedId);
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await sendGeneralNotification({
        title,
        body,
        data: {
          ...(image ? { image } : {}),
          link: 'QuizDetailScreen',
          ...(quizId ? { quizId } : {}),
        },
      });
      setSuccess(true);
      setTitle('');
      setBody('');
      setImage('');
      setQuizId('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send notification');
    }
    setLoading(false);
  };

  return (
    <Box maxWidth={800} mx="auto">
      <Paper elevation={3} sx={{ p: 4, mt: 2 }}>
        <Typography variant="h5" fontWeight={700} mb={2} color="primary">
          Send Quiz Notification
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Send a notification linked to a specific quiz. When users tap the notification, they will be taken directly to the quiz detail page.
        </Typography>

        {/* Filters Section */}
        <Paper elevation={1} sx={{ p: 3, mb: 3, bgcolor: '#f8f9fa' }}>
          <Typography variant="h6" fontWeight={600} mb={2}>
            Filters
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* First row */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Search by title, description, or organizer"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                sx={{ flex: 1, minWidth: 300 }}
                size="small"
              />
              <FormControl sx={{ minWidth: 200 }} size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  label="Category"
                >
                  <MenuItem value="">All Categories</MenuItem>
                  <MenuItem value="general">General</MenuItem>
                  <MenuItem value="technology">Technology</MenuItem>
                  <MenuItem value="business">Business</MenuItem>
                  <MenuItem value="science">Science</MenuItem>
                  <MenuItem value="arts">Arts</MenuItem>
                  <MenuItem value="sports">Sports</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Second row */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Organizer Username"
                value={filters.organizer}
                onChange={(e) => handleFilterChange('organizer', e.target.value)}
                sx={{ minWidth: 200 }}
                size="small"
              />
              <FormControl sx={{ minWidth: 150 }} size="small">
                <InputLabel>Pricing</InputLabel>
                <Select
                  value={filters.pricing}
                  onChange={(e) => handleFilterChange('pricing', e.target.value)}
                  label="Pricing"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="free">Free</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 200 }} size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="registration_open">Registration Open</MenuItem>
                  <MenuItem value="ongoing">Ongoing</MenuItem>
                  <MenuItem value="upcoming">Upcoming</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Third row */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: 200 }} size="small">
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={filters.dateRange}
                  onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                  label="Date Range"
                >
                  <MenuItem value="">All Dates</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="tomorrow">Tomorrow</MenuItem>
                  <MenuItem value="this_week">This Week</MenuItem>
                  <MenuItem value="this_month">This Month</MenuItem>
                  <MenuItem value="next_month">Next Month</MenuItem>
                  <MenuItem value="expired">Expired</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Filter chips */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {Object.entries(filters).map(([key, value]) => 
                value && (
                  <Chip
                    key={key}
                    label={`${key}: ${value}`}
                    onDelete={() => handleFilterChange(key, '')}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )
              )}
              {(Object.values(filters).some(v => v)) && (
                <Button size="small" onClick={clearFilters} color="secondary">
                  Clear All
                </Button>
              )}
            </Box>
          </Box>
        </Paper>

        <Divider sx={{ my: 2 }} />

        {/* Notification Form */}
        <form onSubmit={handleSend}>
          <TextField
            label="Title"
            value={title}
            onChange={e => setTitle(e.target.value)}
            fullWidth
            required
            margin="normal"
          />
          <TextField
            label="Body"
            value={body}
            onChange={e => setBody(e.target.value)}
            fullWidth
            required
            margin="normal"
            multiline
            minRows={2}
          />
          <TextField
            label="Image URL (optional)"
            value={image}
            onChange={e => setImage(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            select
            label="Select Quiz"
            value={quizId}
            onChange={handleQuizSelect}
            fullWidth
            required
            margin="normal"
            disabled={fetchingQuizzes}
            helperText={`Select a quiz to link the notification to its detail page (${quizzes.length} available)`}
          >
            <MenuItem value="">Select a quiz...</MenuItem>
            {fetchingQuizzes ? (
              <MenuItem value="" disabled>
                <CircularProgress size={20} /> Loading quizzes...
              </MenuItem>
            ) : (
              quizzes.map(q => (
                <MenuItem key={q._id} value={q._id}>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {q.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {q.organizer?.firstName} {q.organizer?.lastName} • {q.category} • {q.pricing?.type === 'free' ? 'Free' : `₹${q.pricing?.amount}`}
                    </Typography>
                  </Box>
                </MenuItem>
              ))
            )}
          </TextField>
          <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 2 }} disabled={loading}>
            {loading ? 'Sending...' : 'Send Quiz Notification'}
          </Button>
        </form>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </Paper>
      <Snackbar open={success} autoHideDuration={3000} onClose={() => setSuccess(false)}>
        <Alert severity="success" sx={{ width: '100%' }}>
          Quiz notification sent successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QuizNotificationPage; 